'use strict';
/* ==========================================================================
   levels/jingle.js — step "jingle" (any): ASK THE BASE, the shop jingle as a notebook lyric video (DESIGN.md §5.4).

   ► The song: assets/music/song/ask_the_base.m4a = ACE-Step take lounge_7 (bossa lounge, male lead, 118 bpm, 72 s;
     the user's pick 2026-09-27, assets/CHOICE.md). Timings: assets/music/song/top2_words.json (tools/song_timing.py):
     the TRUE lyric lines of content/jingle.json with word times from the take's Whisper passes (its mishearings
     "so propriator", "acts the base" are mapped back to the sheet), and the beat grid {bpm 118, offset}.

   The whole frame is a pure function of SONG TIME t (the shared <audio> element's clock while it plays; a wall clock
   while audio is locked or missing), so seeking, pausing and the QA screenshots are exact. Every picture beat is keyed
   to a sung word (S.W(line, word)). EVERY NUMBER on screen comes from the till (core-local/till.js), from the video's
   givens (content/script.json s1 {p 120, a 50}, s2 {p 80}, s3 {p 100, u 20}; the $80 sneakers, 25% off, the $10 store
   coupon of s4): see nums().

   Acts, keyed to lyric-line indices (content/jingle.json order):
     intro    0      the radio doodle, ON AIR; "Ask the Base" written on; % pops on "percent"
     coupons  1–2    two 50% OFF coupons; "50% + 50% = FREE?" struck; $120 → × 0.5 → $60 → × 0.5 → $30 (the coupons fly
                     into the arrows); the second half is OF what is left (circled); × 0.5 × 0.5 = × 0.25
     eighty   3–4    20% off → pay 80% → × 0.8 ($80 × 0.8 = $64); "50% + 50% = 100% off" struck; × 0.25 = 75% off
     chorus1  5–8    the hook written big (OF highlighted, BASE circled); the same 20% of two bases; sale-orange price
                     tags popping on the hook words; "a percent is always OF a base"
     updown   9–10   $100 → +20% → $120 → −20% → $96 (not back to $100); the two twenties as coin stacks ($20 vs $24)
     twins    11     discount first / tax first: twin receipts, equal totals
     coupon   12     a $10 store coupon slides onto the receipt BEFORE the tax line prints (Florida)
     chorus2  13–16  the hook again (the example: 6% tax of two bases)
     outro    17–18  the radio; "Nothing's free" ($0.00 struck); the maths card; PAID
   Karaoke (the index card at the bottom): the current line written on word by word in Kalam, the sung word under the
   highlighter, the hook "Ask the base!" in sale orange, the next line faint below.
   Jay (left) and Ms. Rivera (right) are small line-art figures bobbing on the beat; they point on the hook words.
   Controls: click / Space play-pause, ↺ restart (R), ⌂ map, ← → seek 5 s, click or drag the strip at the bottom.
   Portrait phone: the stage on top, Dock.karaoke + ↺ ▶ ⌂ + the strip below. The first full listen → Game.complete.
   Long music (docs/WEBSITE.md §8.4): lesson.json preload.stream names the .m4a, so it plays through the shared
   <audio> element (Audio.media), never decoded into memory.

   QA hooks: sc.song (timings: S(i), E(i), W(i, word), beats), sc.plan.acts, sc.nums, sc.debugAt(t) (freeze at song
   time t), sc.now(), sc.play(t), sc.pause(), sc.toggle(), sc.seek(t), sc.restart(), sc.done() (stamp now),
   sc.lyricAt(t), sc.activeWord(t), sc.actAt(t), sc.heard, sc.ended, sc.audioReady.
   ========================================================================== */
(function () {
  const STEP = 'jingle';
  const TAKE = 'top2', TAKE_NAME = 'lounge_7';
  const AUDIO = 'music/song/ask_the_base.m4a', WORDS = 'music/song/top2_words.json';
  const KEY = 'jingle:' + TAKE;
  const PN = NB.PAL, FONT = NB.FONT;
  const BAND_Y = 846, CUR_Y = 934, NEXT_Y = 1012, CUR_SIZE = 62, NEXT_SIZE = 38;
  const TURN = 0.45;                                   // the page turn between acts (s)
  const CREDIT = 'AI-generated jingle · the maths is real';

  /* ---------------- the numbers (the till only) ---------------- */
  const clean = x => Number((+x).toPrecision(12));
  const D = c => (c % 100 === 0 ? (c < 0 ? '−$' : '$') + Math.abs(c) / 100 : Till.money(c));   // $120 / $4.80
  const X = m => '× ' + clean(m);
  let NUMS = null;
  function nums() {
    if (NUMS) return NUMS;
    const seg = id => { const s = Game.content.script; const g = s && s.segments && s.segments.find(x => x.id === id); return (g && g.question && g.question.given) || {}; };
    const pt = Till.pctText;
    // the lyric's own percents (fifty, twenty); the prices are the video's givens
    const A = 50, P1 = Till.cents(seg('s1').p || 120);
    const c1 = { a: A, m: Till.offMultiplier(A), p: P1, first: Till.discount(P1, A), second: Till.discount(Till.discount(P1, A), A) };
    c1.m2 = clean(c1.m * c1.m); c1.off = Till.successive(A, A); c1.pay = clean(100 - c1.off); c1.sum = A + A;
    const O = 20, P2 = Till.cents(seg('s2').p || 80);
    const e = { off: O, pay: clean(100 - O), m: Till.offMultiplier(O), p: P2, after: Till.discount(P2, O) };
    const U = 20, P3 = Till.cents(seg('s3').p || 100), up = Till.markup(P3, U);
    const u = { u: U, p: P3, up, down: Till.discount(up, U), mu: Till.multiplier(U),
      cut1: Till.pct(P3, U), cut2: Till.pct(up, U) };
    u.md = Till.offMultiplier(U); u.m = clean(u.mu * u.md); u.more = u.cut2 - u.cut1;
    // the chorus examples: the same percent of two bases
    const ch1 = [P3, up].map(b => ({ p: U, base: b, part: Till.pct(b, U) }));
    // the $80 sneakers, 25% off, 6% tax, both orders (as the video's s4); then the $10 store coupon before tax
    const TAX = Till.TAX, SH = 8000, OFF = 25, CP = Till.cents(10), shoe = { label: 'SNEAKERS', cents: SH };
    const disc = Till.receipt({ items: [shoe], discounts: [{ label: `${pt(OFF)} OFF`, pct: OFF }] });
    const tx = Till.tax(SH), withTax = SH + tx, off4 = Till.pct(withTax, OFF), taxFirst = withTax - off4;
    const twins = {
      a: Till.print(disc, { header: 'DISCOUNT FIRST', sub: 'sole proprietor' }), totA: disc.total,
      b: [{ c: 'TAX FIRST', bold: true }, { c: 'sole proprietor', size: 17 }, { rule: true },
        { l: 'SNEAKERS', r: Till.fmt(SH), kind: 'item' }, { l: `TAX ${pt(TAX)}`, r: Till.fmt(tx), kind: 'tax' }, { rule: true },
        { l: 'SUBTOTAL', r: Till.fmt(withTax), kind: 'subtotal' }, { l: `${pt(OFF)} OFF`, r: Till.fmt(-off4), kind: 'discount' },
        { rule: true }, { l: 'TOTAL', r: Till.fmt(taxFirst), kind: 'total', bold: true }, { barcode: true, l: 'taxfirst' }],
      totB: taxFirst, off: OFF, tax: TAX,
    };
    const cr = Till.receipt({ items: [shoe], coupons: [{ label: `${D(CP)} COUPON`, cents: CP }] });
    const coupon = { cents: CP, label: `${D(CP)} OFF`, lines: Till.print(cr, { header: 'SOLE PROPRIETOR', sub: 'store coupon, then tax' }), total: cr.total,
      subtotal: cr.subtotal, tax: cr.tax, rate: TAX };
    const ch2 = [SH, cr.subtotal].map(b => ({ p: TAX, base: b, part: Till.tax(b) }));
    NUMS = { c1, e, u, ch1, ch2, twins, coupon, pt, tags: [X(c1.m), X(e.m), X(u.mu), X(c1.m2), X(Till.multiplier(TAX)), `${pt(OFF)} OFF`] };
    return NUMS;
  }

  /* ---------------- song timing ---------------- */
  const normW = w => String(w).toLowerCase().replace(/[^a-z0-9]/g, '');
  class Song {
    constructor(d) {
      this.d = d; this.lines = d.lines; this.dur = d.duration || 72;
      const b = d.beats || {};
      this.bpm = b.bpm || 118; this.bt = 60 / this.bpm; this.off = b.offset || 0;
      this.items = d.lines.map(l => ({ ...l, kind: 'line' })).concat((d.extras || []).map((x, j) => ({ ...x, i: 100 + j }))).sort((a, c) => a.s - c.s);
    }
    S(i) { return this.lines[i] ? this.lines[i].s : 1e6; }
    E(i) { return this.lines[i] ? this.lines[i].e : 1e6; }
    /* start of the n-th word of line i that matches `word` (exact, then prefix); the line start if none */
    W(i, word, n = 1) {
      const l = this.lines[i]; if (!l) return 1e6;
      const k = normW(word);
      for (const pass of [w => normW(w.w) === k, w => normW(w.w).startsWith(k)]) {
        let c = 0;
        for (const w of l.words) if (pass(w) && ++c === n) return w.s;
      }
      if (window.__songCueMiss) window.__songCueMiss.push(i + ':' + word);
      return l.s;
    }
    beatPos(t) { return (t - this.off) / this.bt; }
    sinceBeat(t) { const p = this.beatPos(t); return (p - Math.floor(p)) * this.bt; }
    pulse(t, k = 9) { return t < this.off - 0.01 ? 0 : Math.exp(-this.sinceBeat(t) * k); }
    singing(t) { return this.lines.some(l => t >= l.s - 0.05 && t <= l.e + 0.1); }
  }

  /* ---------------- small drawing helpers ---------------- */
  const SL = new Map();
  const fontOk = () => !document.fonts || document.fonts.status === 'loaded';
  function art(key, bx, by, bw, bh, draw) {
    let sl = SL.get(key); if (!sl) SL.set(key, sl = {});
    if (FX.sprites && fontOk() && !_spriteBaking && _ctm() && spriteIn(sl, bx, by, bw, bh, draw)) return;
    draw();
  }
  const widths = new Map();
  function tw(str, font) {
    const k = font + '|' + str; let w = widths.get(k);
    if (w === undefined) { g.save(); g.font = font; w = g.measureText(str).width; g.restore(); if (fontOk()) { if (widths.size > 2000) widths.clear(); widths.set(k, w); } }
    return w;
  }
  const hand = (size, w = 700) => `${w} ${size}px ${FONT.hand}`;
  function rrect(x, y, w, h, r) { g.beginPath(); g.moveTo(x + r, y); g.arcTo(x + w, y, x + w, y + h, r); g.arcTo(x + w, y + h, x, y + h, r); g.arcTo(x, y + h, x, y, r); g.arcTo(x, y, x + w, y, r); g.closePath(); }
  function alpha(a, fn) { if (a <= 0.001) return; g.save(); g.globalAlpha *= Math.min(1, a); try { fn(); } finally { g.restore(); } }
  const tag = (text, x, y, t0, o = {}) => Levels.title.priceTag(text, x, y, { t0, rot: o.rot ?? -0.1, size: o.size || 44 });
  /* write text centred at x (handwriting, bold) from t0 over dur */
  const say = (text, x, y, t0, o = {}) => NB.write(text, x, y, { size: o.size || 60, weight: o.weight || 700, align: o.align || 'center', t0, dur: o.dur, color: o.color, font: o.font });
  /* an ink arrow (NB.pen) from a to b, drawn on over [t0, t0 + dur] */
  function arrow(ax, ay, bx, by, t0, o = {}) {
    if (T < t0) return;
    const p = E.outCubic(P(T, t0, t0 + (o.dur || 0.35))), col = o.color || PN.ink, w = o.w || 5;
    const mid = o.bend ? [[ax, ay], [(ax + bx) / 2, (ay + by) / 2 - o.bend], [bx, by]] : [[ax, ay], [bx, by]];
    NB.pen(mid, { w, color: col, prog: p, step: 8 });
    if (p >= 0.98) {
      const [qx, qy] = mid[mid.length - 2], a = Math.atan2(by - qy, bx - qx), k = 22;
      NB.pen([[bx - Math.cos(a - 0.5) * k, by - Math.sin(a - 0.5) * k], [bx, by], [bx - Math.cos(a + 0.5) * k, by - Math.sin(a + 0.5) * k]], { w, color: col, step: 4 });
    }
  }
  /* a store coupon (as levels/video.js): cx,cy = centre; s scale */
  function coupon(big, small, cx, cy, rot = 0, s = 1, a = 1) {
    if (s <= 0 || a <= 0) return;
    g.save(); g.translate(cx, cy); g.rotate(rot); g.scale(s, s); g.globalAlpha *= a;
    art('jcoupon|' + big + '|' + small, -142, -64, 284, 128, () => {
      g.fillStyle = 'rgba(29,43,79,0.12)'; g.fillRect(-128, -46, 260, 104);
      g.fillStyle = PN.card; g.fillRect(-130, -52, 260, 104);
      g.save(); g.setLineDash([10, 7]); g.lineWidth = 3; g.strokeStyle = PN.ink; g.strokeRect(-120, -42, 240, 84); g.restore();
      g.fillStyle = PN.ink; g.textAlign = 'center'; g.textBaseline = 'middle';
      g.font = `700 44px ${FONT.ui}`; g.fillText(big, 0, -6);
      g.font = `400 17px ${FONT.mono}`; g.fillStyle = PN.pencil; g.fillText(small, 0, 28);
    });
    g.restore();
  }

  /* the radio doodle (fine-liner): x,y = the body's centre. on = the ON AIR lamp; pu = the beat pulse (sound waves) */
  function radio(x, y, s, on, pu) {
    if (s <= 0) return;
    g.save(); g.translate(x, y); g.scale(s, s);
    art('radio', -250, -250, 520, 420, () => {
      g.lineJoin = 'round'; g.lineCap = 'round'; g.strokeStyle = PN.ink;
      // antenna
      g.lineWidth = 5; g.beginPath(); g.moveTo(90, -120); g.lineTo(170, -230); g.stroke();
      g.fillStyle = PN.ink; g.beginPath(); g.arc(170, -232, 9, 0, TAU); g.fill();
      // body + feet
      g.fillStyle = PN.card; rrect(-230, -120, 460, 250, 34); g.fill(); g.lineWidth = 6; g.stroke();
      g.lineWidth = 5; g.beginPath(); g.moveTo(-170, 130); g.lineTo(-180, 158); g.moveTo(170, 130); g.lineTo(180, 158); g.stroke();
      // speaker grille
      g.lineWidth = 3;
      for (let r = 20; r <= 86; r += 22) { g.beginPath(); g.arc(-110, 8, r, 0, TAU); g.stroke(); }
      g.fillStyle = PN.ink; g.beginPath(); g.arc(-110, 8, 9, 0, TAU); g.fill();
      // dial window with ticks
      g.fillStyle = PN.page; rrect(0, -78, 200, 72, 10); g.fill(); g.lineWidth = 4; g.stroke();
      g.lineWidth = 2; g.beginPath();
      for (let i = 0; i <= 16; i++) { const tx = 14 + i * 10.8; g.moveTo(tx, -78); g.lineTo(tx, i % 4 ? -64 : -56); }
      g.stroke();
      g.font = `400 15px ${FONT.mono}`; g.fillStyle = PN.pencil; g.textAlign = 'center'; g.textBaseline = 'middle';
      ['88', '96', '104'].forEach((f, i) => g.fillText(f, 28 + i * 72, -24));
      // knobs
      g.lineWidth = 4; g.fillStyle = PN.card;
      [[40, 70], [150, 70]].forEach(([kx, ky]) => { g.beginPath(); g.arc(kx, ky, 26, 0, TAU); g.fill(); g.stroke(); g.beginPath(); g.moveTo(kx, ky); g.lineTo(kx + 12, ky - 20); g.stroke(); });
      g.font = `700 18px ${FONT.ui}`; g.fillStyle = PN.ink; g.fillText('SOLE FM', 96, 112);
    });
    // the needle (red pen) sits on the lesson's frequency
    NB.pen([[130, -74], [130, -12]], { w: 4, color: PN.redpen, step: 8 });
    // ON AIR sign
    g.save(); g.translate(-10, -190);
    rrect(-110, -34, 220, 68, 14); g.fillStyle = on ? PN.redpen : PN.card; g.fill(); g.lineWidth = 5; g.strokeStyle = PN.ink; g.stroke();
    g.font = `700 40px ${FONT.ui}`; g.fillStyle = on ? '#fff' : PN.pencilLight; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText('ON AIR', 0, 2);
    g.restore();
    if (on) {
      g.lineWidth = 3; g.strokeStyle = PN.redpen; g.lineCap = 'round';
      [-150, 130].forEach(sx => { g.beginPath(); g.moveTo(sx * 0.9, -190); g.lineTo(sx, -190); g.stroke(); });
      // sound waves on the beat
      for (let k = 0; k < 3; k++) {
        const r = 60 + k * 44 + 16 * pu, a = (0.8 - k * 0.22) * (0.5 + 0.5 * pu);
        g.save(); g.globalAlpha *= a; g.lineWidth = 6; g.strokeStyle = PN.ink;
        g.beginPath(); g.arc(210, 0, r, -0.6, 0.6); g.stroke();
        g.beginPath(); g.arc(-250, 0, r, Math.PI - 0.6, Math.PI + 0.6); g.stroke();
        g.restore();
      }
    }
    g.restore();
  }

  /* ======================================================================
     act: intro (line 0)
     ====================================================================== */
  function actIntro(S, t, A) {
    NB.ground();
    const on = t > S.W(0, 'This') - 0.1;
    radio(960, 500, 1.05, on, S.pulse(t, 7));
    NB.title('Ask the Base', 960, 180, { size: 96, t0: 0.15, hi: 'Base' });
    const tS = S.W(0, 'Sole'), tP = S.W(0, 'percent');
    if (t > tS) NB.write('a word from Sole Proprietor', 960, 790, { font: 'ui', size: 40, color: PN.pencil, align: 'center', t0: tS, dur: 0.4 });
    if (t > tP) tag('%', 1330, 330, tP, { size: 80, rot: 0.14 });
  }

  /* ======================================================================
     act: coupons (lines 1–2): two 50% OFF coupons are × 0.5 × 0.5, not free
     ====================================================================== */
  const CHAIN_Y = 520, CX = [560, 960, 1360];
  function actCoupons(S, t, A) {
    NB.ground();
    const N = nums().c1, big = `${N.a}% OFF`;
    const tF1 = S.W(1, 'fifty'), tF2 = S.W(1, 'fifty', 2), tMk = S.W(1, 'make'), tFr = S.W(1, 'free');
    const tThe = S.W(2, 'The'), tSe = S.W(2, 'second'), tTk = S.W(2, 'takes'), tHa = S.W(2, 'half'), tLe = S.W(2, 'left'), tYo = S.W(2, 'you');
    // the price
    tag(D(N.p), CX[0], CHAIN_Y, Math.max(A.at + 0.1, tF1 - 0.2), { rot: -0.06, size: 50 });
    // the two coupons slide in, then fly into the two arrows' labels
    const fly = (t0, from, to) => { const u = E.inOutCubic(P(t, t0, t0 + 0.5)); return { x: lerp(from[0], to[0], u), y: lerp(from[1], to[1], u), s: lerp(1, 0.45, u), a: 1 - P(t, t0 + 0.35, t0 + 0.5) }; };
    const L1 = [(CX[0] + CX[1]) / 2, CHAIN_Y - 74], L2 = [(CX[1] + CX[2]) / 2, CHAIN_Y - 74];
    [[tF1, [640, 250], -0.08, tThe, L1], [tF2, [1010, 250], 0.07, tTk, L2]].forEach(([t0, at, rot, tf, to], i) => {
      if (t < t0) return;
      const inU = E.outCubic(P(t, t0, t0 + 0.4)), f = fly(tf, [lerp(at[0] + 500, at[0], inU), at[1]], to);
      coupon(big, 'store coupon', f.x, f.y, rot * (1 - P(t, tf, tf + 0.5)), f.s, f.a);
    });
    // the claim: 50% + 50% = FREE?  (struck on "free")
    if (t > tMk) {
      NB.sticky(`${N.a}% + ${N.a}%\n= ${N.sum}% off\n= FREE?`, 1270, 160, { w: 330, rot: 0.03, t0: tMk, size: 38 });
      NB.strike(1284, 318, 1476, 326, { t0: tFr + 0.05, dur: 0.3 });
      NB.note('nope', 1520, 330, -0.08, { t0: tFr + 0.3, size: 44 });
    }
    // the chain: $120 × 0.5 → $60 × 0.5 → $30
    arrow(CX[0] + 100, CHAIN_Y, CX[1] - 100, CHAIN_Y, tThe + 0.3);
    if (t > tThe + 0.35) say(X(N.m), L1[0], L1[1], tThe + 0.35, { size: 50 });
    tag(D(N.first), CX[1], CHAIN_Y, tSe, { rot: 0.05, size: 50 });
    arrow(CX[1] + 100, CHAIN_Y, CX[2] - 100, CHAIN_Y, tTk + 0.3);
    if (t > tTk + 0.35) say(X(N.m), L2[0], L2[1], tTk + 0.35, { size: 50 });
    tag(D(N.second), CX[2], CHAIN_Y, tHa, { rot: -0.04, size: 50 });
    // "from what is left": the second coupon's base is the $60
    if (t > tLe) {
      NB.circle(CX[1] + 6, CHAIN_Y, 118, 62, { t0: tLe, dur: 0.35 });
      NB.note('the base: what is left', CX[1], CHAIN_Y + 104, 0.02, { t0: tLe + 0.2, align: 'center', size: 36 });
    }
    // × 0.5 × 0.5 = × 0.25: pay 25%
    if (t > tYo) {
      const eq = `${X(N.m)} ${X(N.m)} = ${X(N.m2)}`, f = hand(76), w = tw(eq, f), x0 = 960 - w / 2, hw = tw(X(N.m2), f);
      NB.highlight(x0 + w - hw - 8, 720 - 30, hw + 16, 60, { t0: tYo + 0.5, dur: 0.3 });
      NB.write(eq, x0, 720, { size: 76, weight: 700, t0: tYo, dur: 0.6 });
      NB.write(`you pay ${N.pay}%, not 0%`, x0 + w + 30, 726, { font: 'ui', size: 34, color: PN.pencil, t0: tYo + 0.7, dur: 0.3 });
    }
  }

  /* ======================================================================
     act: eighty (lines 3–4): 20% off = pay 80% = × 0.8; multiply, don't add
     ====================================================================== */
  function actEighty(S, t, A) {
    NB.ground();
    const N = nums(), e = N.e, c = N.c1;
    const tTw = S.W(3, 'Twenty'), tEi = S.W(3, 'eighty'), tPo = S.W(3, 'point'), tGo = S.W(3, 'go');
    const tMu = S.W(4, 'Multiply'), tAd = S.W(4, 'add'), tUp = S.W(4, 'up'), tNo = S.W(4, 'now'), tKn = S.W(4, 'know');
    const Y1 = 250;
    tag(`${e.off}% OFF`, 520, Y1, tTw - 0.05, { rot: -0.08, size: 46 });
    arrow(660, Y1, 790, Y1, tTw + 0.25);
    if (t > tEi) say(`pay ${e.pay}%`, 930, Y1, tEi, { size: 64 });
    arrow(1080, Y1, 1190, Y1, tEi + 0.3);
    if (t > tPo) {
      const f = hand(70), w = tw(X(e.m), f);
      NB.highlight(1225, Y1 - 30, w + 20, 60, { t0: tPo + 0.35 });
      NB.write(X(e.m), 1235, Y1, { size: 70, weight: 700, t0: tPo, dur: 0.4 });
    }
    if (t > tGo) NB.write(`${D(e.p)} ${X(e.m)} = ${D(e.after)}`, 960, Y1 + 96, { size: 44, weight: 400, align: 'center', color: PN.pencil, t0: tGo, dur: 0.5 });
    // multiply, don't add
    const Y2 = 500, Y3 = 640;
    if (t > tMu) {
      const bad = `${c.a}% + ${c.a}% = ${c.sum}% off`;
      NB.write(bad, 960, Y2, { size: 64, weight: 700, align: 'center', t0: tMu, dur: 0.5 });
      const w = tw(bad, hand(64));
      NB.strike(960 - w / 2 - 10, Y2 + 12, 960 + w / 2 + 10, Y2 - 12, { t0: tAd, dur: 0.3 });
      NB.note("don't add", 960 + w / 2 + 40, Y2 - 30, -0.1, { t0: tUp, size: 40 });
    }
    if (t > tNo) {
      const good = `${X(c.m)} ${X(c.m)} = ${X(c.m2)}`, f = hand(72), w = tw(good, f), x0 = 960 - w / 2;
      NB.write(good, x0, Y3, { size: 72, weight: 700, t0: tNo, dur: 0.5 });
      NB.write(`= ${c.off}% off`, x0 + w + 30, Y3, { size: 56, weight: 700, color: PN.redpen, t0: tNo + 0.4, dur: 0.3 });
      NB.highlight(x0 + w + 20, Y3 - 28, tw(`= ${c.off}% off`, hand(56)) + 20, 56, { t0: tKn });
      NB.tick(x0 - 50, Y3, { s: 1.4, t0: tKn });
    }
  }

  /* ======================================================================
     act: the chorus (lines c0..c0+3): the hook written big; the same percent of two bases; price tags popping
     ====================================================================== */
  const TAG_SPOTS = [[470, 250, -0.12], [1450, 250, 0.1], [470, 520, 0.08], [1450, 500, -0.1], [560, 740, -0.05], [880, 750, 0.1]];
  function actChorus(S, t, A) {
    NB.ground();
    const c0 = A.c0, N = nums(), ex = A.c0 === 5 ? N.ch1 : N.ch2;
    // "Percent OF what? Ask the base!" (line c0): OF highlighted, BASE circled
    const f = hand(88), h1 = 'Percent OF what?', h2 = 'Ask the BASE!';
    const w1 = tw(h1, f), w2 = tw(h2, f), Y1 = 250, Y2 = 360;
    const tPe = S.W(c0, 'Percent'), tOf = S.W(c0, 'of'), tWh = S.W(c0, 'what'), tAs = S.W(c0, 'Ask'), tBa = S.W(c0, 'base');
    const ofX = 960 - w1 / 2 + tw('Percent ', f), ofW = tw('OF', f);
    NB.highlight(ofX - 8, Y1 - 36, ofW + 16, 72, { t0: tOf + 0.1, dur: 0.25 });
    NB.write(h1, 960, Y1, { size: 88, weight: 700, align: 'center', t0: tPe, dur: Math.max(0.4, tWh + 0.3 - tPe) });
    NB.write(h2, 960, Y2, { size: 88, weight: 700, align: 'center', t0: tAs, dur: Math.max(0.3, tBa + 0.3 - tAs) });
    if (t > tBa) NB.circle(960 + w2 / 2 - tw('BASE!', f) / 2 - 6, Y2, tw('BASE!', f) / 2 + 26, 58, { t0: tBa + 0.1, dur: 0.35 });
    // "Change the base, you change the case" (c0+1): the same percent of two bases, the bases circled in red
    const tCh = S.W(c0 + 1, 'Change'), tCa = S.W(c0 + 1, 'case');
    [[ex[0], tCh, 490], [ex[1], tCa, 580]].forEach(([r, t0, y]) => {
      if (t < t0) return;
      const pre = `${N.pt(r.p)} of `, base = D(r.base), post = ` = ${D(r.part)}`, fs = hand(60);
      const w = tw(pre + base + post, fs), x0 = 960 - w / 2;
      NB.write(pre + base + post, x0, y, { size: 60, weight: 700, t0, dur: 0.5 });
      const bx = x0 + tw(pre, fs), bw = tw(base, fs);
      NB.circle(bx + bw / 2, y, bw / 2 + 18, 40, { t0: t0 + 0.45, dur: 0.3 });
      const px = x0 + tw(pre + base + ' = ', fs), pw = tw(D(r.part), fs);
      NB.highlight(px - 6, y - 26, pw + 12, 52, { t0: t0 + 0.6 });
    });
    if (t > tCa + 0.4) NB.note('new base, new answer', 960, 660, -0.02, { t0: tCa + 0.4, size: 38, align: 'center' });
    // the hook again (c0+2): sale-orange price tags pop on the hook words, on the beat
    const words = S.lines[c0 + 2] ? S.lines[c0 + 2].words : [];
    words.forEach((w, j) => {
      const sp = TAG_SPOTS[j % TAG_SPOTS.length], txt = N.tags[j % N.tags.length];
      if (t < w.s) return;
      const settle = 1 + 0.08 * Math.exp(-(t - w.s) * 5) * Math.sin((t - w.s) * 18);
      g.save(); g.translate(sp[0], sp[1]); g.scale(settle, settle); g.translate(-sp[0], -sp[1]);
      tag(txt, sp[0], sp[1], w.s, { rot: sp[2], size: 46 });
      g.restore();
    });
    // "Every percent has its place" (c0+3): the rule on a sticky note
    const tEv = S.W(c0 + 3, 'Every'), tPl = S.W(c0 + 3, 'place');
    if (t > tEv) NB.sticky('a percent is\nalways OF a base', 1150, 630, { w: 360, rot: -0.04, t0: tEv, size: 40 });
    if (t > tPl) NB.tick(1470, 660, { s: 1.5, t0: tPl });
  }

  /* ======================================================================
     act: updown (lines 9–10): +20% then −20% = $96; the second twenty is bigger
     ====================================================================== */
  function actUpDown(S, t, A) {
    NB.ground();
    const u = nums().u, pt = nums().pt;
    const tUp = S.W(9, 'Up'), tT1 = S.W(9, 'twenty'), tDn = S.W(9, 'down'), tT2 = S.W(9, 'twenty', 2), tBk = S.W(9, 'back');
    const tSe = S.W(10, 'second'), tMo = S.W(10, 'more'), tBi = S.W(10, 'bigger'), tSt = S.W(10, 'stack');
    const P0 = [560, 380], P1 = [960, 230], P2 = [1360, 380];
    tag(D(u.p), P0[0], P0[1], Math.max(A.at + 0.1, tUp - 0.1), { rot: -0.06, size: 50 });
    arrow(P0[0] + 90, P0[1] - 50, P1[0] - 110, P1[1] + 10, tUp + 0.2, { bend: 30 });
    if (t > tT1) NB.write(`+${pt(u.u)}  (${X(u.mu)})`, 735, 212, { size: 40, weight: 700, align: 'center', color: PN.ink, t0: tT1, dur: 0.4 });
    tag(D(u.up), P1[0], P1[1], tT1 + 0.2, { rot: 0.05, size: 50 });
    arrow(P1[0] + 110, P1[1] + 10, P2[0] - 90, P2[1] - 50, tDn + 0.1, { bend: -30 });
    if (t > tT2) NB.write(`−${pt(u.u)}  (${X(u.md)})`, 1240, 250, { size: 40, weight: 700, align: 'center', color: PN.ink, t0: tT2, dur: 0.4 });
    tag(D(u.down), P2[0], P2[1], tT2 + 0.2, { rot: -0.05, size: 50 });
    if (t > tBk) {
      NB.note(`not back to ${D(u.p)}`, P2[0] + 10, P2[1] + 80, 0.03, { t0: tBk, align: 'center', size: 38 });
      NB.write(`${X(u.mu)} ${X(u.md)} = ${X(u.m)}`, 960, 400, { size: 40, weight: 400, align: 'center', color: PN.pencil, t0: tBk + 0.2, dur: 0.4 });
    }
    // the two twenties as coin stacks: 20% of $100 vs 20% of $120 (one coin = $2)
    const COIN = 200, stacks = [[600, u.cut1, u.p, tSe], [1240, u.cut2, u.up, tMo]];   // far enough apart that each ~380 px label sits over its own stack
    stacks.forEach(([x, part, base, t0], i) => {
      if (t < t0) return;
      const n = Math.round(part / COIN), shown = Math.min(n, Math.floor((t - t0) / 0.05) + 1), baseY = 800;
      g.save(); g.lineWidth = 4; g.strokeStyle = PN.ink;
      for (let k = 0; k < shown; k++) {
        const y = baseY - k * 15;
        g.fillStyle = i ? '#f6e7a6' : '#f3edd6';
        g.beginPath(); g.ellipse(x, y, 62, 14, 0, 0, TAU); g.fill(); g.stroke();
      }
      g.restore();
      const top = baseY - n * 15 - 50;
      NB.write(`${pt(u.u)} of ${D(base)} = ${D(part)}`, x, top, { size: 40, weight: 700, align: 'center', t0: t0 + 0.2, dur: 0.4 });
      if (i === 1 && t > tBi) {
        NB.highlight(x - 190, top - 22, 380, 44, { t0: tBi, color: PN.hi });
        NB.note(`${D(u.more)} bigger`, x + 200, baseY - 100, -0.06, { t0: tSt, size: 38 });
      }
    });
  }

  /* ======================================================================
     act: twins (line 11): discount first or tax first, the same total
     ====================================================================== */
  function hOf(it, size) { const lh = Math.round(size * 1.9); return it.rule ? lh * 0.6 : it.barcode ? 90 : Math.round((it.size || size) * 1.9); }
  function rowY(lines, k, size) { let y = 24; for (let i = 0; i < k; i++) y += hOf(lines[i], size); return y + hOf(lines[k], size) / 2; }
  function actTwins(S, t, A) {
    NB.ground();
    const T2 = nums().twins, SZ = 19, W = 330;
    const tDi = S.W(11, 'Discount'), tTx = S.W(11, 'tax'), tTo = S.W(11, 'total'), tSa = S.W(11, 'same');
    const side = [[T2.a, 500, tDi - 0.1, T2.totA, -0.02], [T2.b, 1090, tTx - 0.1, T2.totB, 0.02]];
    side.forEach(([lines, x, t0, tot, rot]) => {
      g.save(); g.translate(x, 205); g.rotate(rot);
      NB.receipt(lines, 0, 0, { w: W, size: SZ, t0, lineDur: 0.07 });
      const k = lines.findIndex(l => l.kind === 'total');
      if (k >= 0 && t > tTo) NB.highlight(W - 20 - tw(Till.fmt(tot), `600 ${SZ}px ${FONT.mono}`) - 10, rowY(lines, k, SZ) - 16, tw(Till.fmt(tot), `600 ${SZ}px ${FONT.mono}`) + 20, 32, { t0: tTo });
      g.restore();
    });
    if (t > tSa) {
      say("=", 960, 480, tSa, { size: 130 });
      NB.tick(960, 560, { s: 1.8, t0: tSa + 0.3 });
      NB.note(`${D(T2.totA)} both ways`, 960, 640, -0.03, { t0: tSa + 0.4, align: 'center', size: 40 });
    }
  }

  /* ======================================================================
     act: coupon (line 12): the store coupon comes off BEFORE the tax line (Florida)
     ====================================================================== */
  function actCoupon(S, t, A) {
    NB.ground();
    const C = nums().coupon, SZ = 21, W = 380, RX = 660, RY = 210, LD = 0.1;
    const tCo = S.W(12, 'coupons'), tBe = S.W(12, 'before'), tTx = S.W(12, 'tax'), tFl = S.W(12, "that's");
    const k = C.lines.findIndex(l => l.kind === 'coupon');
    // the items print first; the coupon lands on its row, THEN the subtotal and the tax print
    const land = tBe;
    const lines = t < land ? C.lines.slice(0, k) : C.lines, t0 = t < land ? A.at + 0.1 : Math.min(A.at + 0.1, land - k * LD);
    NB.receipt(lines, RX, RY, { w: W, size: SZ, t0, lineDur: LD });
    const cy = RY + rowY(C.lines, k, SZ);
    if (t > tCo - 0.2) {
      const u = E.inOutCubic(P(t, tCo - 0.2, land)), a = 1 - P(t, land + 0.1, land + 0.4);
      coupon(C.label, 'store coupon', lerp(1560, RX + W + 170, u), lerp(260, cy, u), lerp(0.2, 0, u), 1, a);
    }
    if (t > land) {
      arrow(RX + W + 60, cy, RX + W + 8, cy, land + 0.05, { color: PN.redpen, dur: 0.2 });
      NB.note('coupon first…', RX + W + 80, cy + 4, -0.03, { t0: land + 0.1, size: 38 });
      const kt = C.lines.findIndex(l => l.kind === 'tax');
      const ty = RY + rowY(C.lines, kt, SZ);
      if (t > tTx) {
        NB.note(`…then ${C.rate}% tax on ${D(C.subtotal)}`, RX + W + 80, ty + 4, -0.02, { t0: tTx + 0.1, size: 38 });
        NB.highlight(RX + 14, ty - 17, W - 28, 34, { t0: tTx + 0.05 });
      }
    }
    if (t > tFl) NB.sticky('Florida: store coupons\ncome off BEFORE tax', 200, 240, { w: 400, rot: -0.06, t0: tFl, size: 36 });
  }

  /* ======================================================================
     act: outro (lines 17–18 + the band's tail): nothing's free; the maths card; PAID
     ====================================================================== */
  function actOutro(S, t, A) {
    NB.ground();
    const N = nums(), last = S.lines.length - 1;
    const tSo = S.W(17, 'Sole'), tNo = S.W(17, 'Nothing'), tFr = S.W(17, 'free'), tBu = S.W(18, 'But'), tMa = S.W(18, 'maths'), tEnd = S.E(last);
    radio(560, 470, 0.9, S.singing(t) || t < tEnd + 0.5, S.pulse(t, 7));
    if (t > tSo) NB.write('SOLE PROPRIETOR', 560, 740, { size: 52, weight: 700, align: 'center', t0: tSo, dur: 0.6 });
    if (t > tNo) {
      tag(D(0), 880, 230, tNo, { rot: 0.12, size: 50 });
      NB.strike(800, 260, 970, 200, { t0: tFr, dur: 0.25 });
      NB.note("nothing's free", 900, 310, 0.06, { t0: tFr + 0.2, size: 36, align: 'center' });
    }
    // the maths card
    if (t > tBu - 0.2) {
      const x = 1020, y = 170, w = 720, h = 470;
      const s = E.outBack(P(t, tBu - 0.2, tBu + 0.2), 1.3);
      g.save(); g.translate(x + w / 2, y + h / 2); g.scale(s, s); g.translate(-(x + w / 2), -(y + h / 2));
      NB.indexCard(x, y, w, h, { header: 90, pitch: 70 });
      NB.write("the maths (we'll show you)", x + 30, y + 48, { font: 'ui', size: 34, weight: 700, t0: tBu, dur: 0.4 });
      const c = N.c1, u = N.u, C = N.coupon;
      const rows = [
        `${c.a}% then ${c.a}% off:  ${X(c.m)} ${X(c.m)} = ${X(c.m2)}`,
        `+${u.u}% then −${u.u}%:  ${X(u.mu)} ${X(u.md)} = ${X(u.m)}`,
        `${D(C.cents)} coupon, then ${C.rate}% tax`,
        'a percent is always OF a base',
      ];
      rows.forEach((r, i) => NB.write(r, x + 30, y + 90 + 70 * (i + 1) - 24, { size: 40, weight: i === 3 ? 700 : 400, t0: tMa + 0.1 + i * 0.45, dur: 0.45, color: i === 3 ? PN.redpen : PN.ink }));
      g.restore();
    }
    if (t > tEnd + 2.2) UI.stamp('PAID', 1500, 600, { s: E.outBack(P(t, tEnd + 2.2, tEnd + 2.5), 1.6), rot: -0.12 });
  }

  /* ---------------- the acts' plan (from the take's own line times) ---------------- */
  function plan(S) {
    const b = i => Math.max(S.E(i - 1) - 0.1, S.S(i) - 0.35);     // never before the previous line's last word
    const acts = [
      { id: 'intro', at: 0, draw: actIntro },
      { id: 'coupons', at: b(1), draw: actCoupons },
      { id: 'eighty', at: b(3), draw: actEighty },
      { id: 'chorus1', at: b(5), draw: actChorus, c0: 5 },
      { id: 'updown', at: b(9), draw: actUpDown },
      { id: 'twins', at: b(11), draw: actTwins },
      { id: 'coupon', at: S.W(12, 'store') - 0.2, draw: actCoupon },        // after "the same" (lines 11–12 are back to back)
      { id: 'chorus2', at: b(13), draw: actChorus, c0: 13 },
      { id: 'outro', at: b(17), draw: actOutro },
    ];
    acts.forEach((a, i) => { a.end = i + 1 < acts.length ? acts[i + 1].at : S.dur; });
    return { acts, ticks: acts.slice(1).map(a => a.at) };
  }
  function actAt(p, t) { let k = 0; for (let i = 0; i < p.acts.length; i++) if (t >= p.acts[i].at) k = i; return k; }
  /* the notebook page turn: the old page slides up off the top (the next page is already underneath) */
  function turn(drawOld, drawNew, t, t0, dur) {
    const p = E.inOutCubic(P(t, t0, t0 + dur));
    drawNew();
    if (p >= 1) return;
    const dy = -1100 * p;
    g.save(); g.beginPath(); g.rect(0, 0, 1920, 1080 + dy); g.clip(); g.translate(0, dy); drawOld(); g.restore();
    g.save(); g.fillStyle = 'rgba(29,43,79,0.12)'; g.fillRect(0, 1080 + dy, 1920, 8); g.restore();
  }
  function drawActs(S, p, t) {
    const k = actAt(p, t), A = p.acts[k];
    const run = i => () => { g.save(); p.acts[i].draw(S, t, p.acts[i]); g.restore(); g.globalAlpha = 1; };
    if (k > 0 && t < A.at + TURN) turn(run(k - 1), run(k), t, A.at, TURN);
    else run(k)();
  }

  /* ---------------- the cast: Jay (left) and Ms. Rivera (right), bobbing on the beat ---------------- */
  const POSE_DX = { rivera_point: -131.5 / 1100, jay_point: 48.5 / 1100 };     // as levels/video.js (the body stays put)
  function puppet(name, x, y, h, pose, facing, env, t, rot) {
    const img = (Puppet.POSES[name] || {})[pose] || Puppet.DEFAULT[name] || name, native = Puppet.NATIVE[img] || 0;
    const flip = native !== 0 && Math.sign(native) !== Math.sign(facing);
    Puppet.draw(name, x - (POSE_DX[img] || 0) * h * (flip ? -1 : 1), y, h, { env, facing, t, pose, rot });
  }
  function cast(S, t, docked) {
    const pu = S.pulse(t, 7), bar = Math.floor(S.beatPos(t) / 4), sway = (bar % 2 ? 1 : -1) * 0.02, bob = 10 * pu;
    const near = (i, w, n) => { const x = S.W(i, w, n); return t > x && t < x + 1.0; };
    const hook = [5, 7, 13, 15].some(c => near(c, 'what') || near(c, 'Ask') || near(c, 'base'));
    const jayPt = hook || near(1, 'free') || near(9, 'back');
    const rivPt = hook || near(2, 'left') || near(4, 'Multiply') || near(12, 'before') || near(18, 'maths');
    const y = docked ? 1080 : BAND_Y + 20, h = docked ? 380 : 330;
    puppet('jay', 250, y - bob, h, jayPt ? 'point' : null, 1, 0.15 + 0.85 * pu, t, sway);
    puppet('rivera', 1740, y - bob, h * 1.05, rivPt ? 'point' : null, -1, 0.15 + 0.85 * pu, t, -sway);
  }

  /* ---------------- karaoke ---------------- */
  const HOOK = new Set(['ask', 'the', 'base']);
  /* the hook words of a chorus line: "Ask the base!" (sale orange) */
  function hookIdx(item) {
    const ws = item.words, out = new Set();
    if (item.section !== 'Chorus') return out;
    for (let k = 0; k + 2 < ws.length; k++) if (['ask', 'the', 'base'].every((w, j) => normW(ws[k + j].w) === w)) { out.add(k); out.add(k + 1); out.add(k + 2); }
    return out;
  }
  const layouts = new Map();
  function layout(item, size) {
    const key = item.i + '|' + item.s + '|' + size;
    if (layouts.has(key)) return layouts.get(key);
    const f = hand(size), sp = tw(' ', f), hk = hookIdx(item);
    let x = 0;
    const toks = item.words.map((w, j) => { const ww = tw(w.w, f), tk = { text: w.w, s: w.s, e: w.e, x, w: ww, hook: hk.has(j) }; x += ww + sp; return tk; });
    const out = { toks, width: x - sp, size };
    if (fontOk()) layouts.set(key, out);
    return out;
  }
  const wordEnd = (ws, j) => { const nx = ws[j + 1]; return Math.min(Math.max(ws[j].e, ws[j].s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); };
  function drawCurrent(item, t, y, maxW = 1640) {
    let size = CUR_SIZE, Lo = layout(item, size);
    if (Lo.width > maxW) { size = Math.floor(size * maxW / Lo.width); Lo = layout(item, size); }
    const x0 = 960 - Lo.width / 2, appear = item.appear ?? item.s - 0.5, a = E.outCubic(P(t, appear, appear + 0.25));
    const yy = y + (1 - a) * 30;
    Lo.toks.forEach((tk, j) => {
      const x = x0 + tk.x, sung = t >= tk.s, active = t >= tk.s - 0.02 && t < wordEnd(Lo.toks, j);
      if (active) NB.highlight(x - 8, yy - size * 0.42, tk.w + 16, size * 0.84, { t0: tk.s - 0.02, dur: 0.12 });
      if (!sung || t < tk.s + 0.3) NB.write(tk.text, x, yy, { size, weight: 700, color: PN.pencilLight, alpha: a });
      if (sung) NB.write(tk.text, x, yy, { size, weight: 700, color: tk.hook ? PN.sale : PN.ink, t0: tk.s, dur: clamp(tk.e - tk.s, 0.08, 0.3) });
    });
  }
  /* a line comes up 0.5 s before its first word, but never before the previous line has been sung */
  const appearAt = (it, k) => (k === 0 ? it[0].s - 0.5 : Math.min(it[k].s, Math.max(it[k].s - 0.5, it[k - 1].e + 0.05)));
  function lyricAt(song, t) {
    const it = song.items;
    let cur = -1;
    for (let k = 0; k < it.length; k++) if (t >= appearAt(it, k)) cur = k;
    let c = cur >= 0 ? it[cur] : null;
    if (c) c.appear = appearAt(it, cur);
    const nx = it[cur + 1] || null;
    if (c && t > c.e + 2.2 && (!nx || nx.s - t > 1.2)) c = null;       // a long instrumental gap: clear the line
    return { cur: c, next: nx && nx.s - t < 8 ? nx : null };
  }
  /* the word being sung at t (QA: the karaoke's active word) */
  function activeWord(song, t) {
    const { cur } = lyricAt(song, t); if (!cur) return null;
    const ws = cur.words;
    for (let j = 0; j < ws.length; j++) if (t >= ws[j].s - 0.02 && t < wordEnd(ws, j)) return { line: cur.i, j, w: ws[j].w, s: ws[j].s, e: ws[j].e };
    return null;
  }
  function band(song, t) {
    NB.indexCard(40, BAND_Y, 1840, 260, { header: 44, pitch: 78 });
    const { cur, next } = lyricAt(song, t);
    const sec = (cur || next || {}).section;
    if (sec) NB.write(sec.toUpperCase(), 70, BAND_Y + 23, { font: 'ui', size: 22, weight: 700, color: PN.pencil });
    // (the credit is drawn once, top left under the title)
    if (cur) drawCurrent(cur, t, CUR_Y);
    if (next) {
      let size = NEXT_SIZE; const w = tw(next.text, hand(size, 400));
      if (w > 1640) size = Math.floor(size * 1640 / w);
      NB.write(next.text, 960, NEXT_Y, { size, weight: 400, color: PN.pencil, align: 'center', alpha: 0.8 });
    } else if (song.items.length && t > song.items[song.items.length - 1].e) {
      NB.write('Percent OF what? Ask the base.', 960, NEXT_Y, { size: 40, weight: 700, color: PN.pencil, align: 'center', t0: song.items[song.items.length - 1].e + 0.3 });
    }
    if (!cur && !next && t > 0.6) {
      const b = ((Math.floor(song.beatPos(t)) % 3) + 3) % 3, pu = song.pulse(t, 6);
      ['♪', '♫', '♪'].forEach((n, i) => { g.save(); g.font = `64px 'Segoe UI Symbol', 'DejaVu Sans', sans-serif`; g.fillStyle = PN.ink; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(n, 860 + i * 100, CUR_Y - 22 * pu * (i === b ? 1 : 0.15)); g.restore(); });
    }
  }

  /* ======================================================================
     the scene
     ====================================================================== */
  class Jingle extends Scene {
    enter() {
      this.take = TAKE;
      this.song = null; this.plan = null; this.err = null;
      this.playing = false; this.frozen = false; this.ended = false; this.startedOnce = false;
      this.pos = 0; this.t0ctx = null; this.heard = 0;
      this.audioReady = false; this.completed = false; this.gone = false;
      Audio.stopMusic();
      try { this.nums = nums(); } catch (e) { this.err = 'till: ' + e.message; console.warn('[jingle]', this.err); }
      const wurl = Assets.url(WORDS);
      const words = (window.Loader ? Loader.json(wurl).then(d => d || Promise.reject(new Error('no timings'))) : fetch(wurl).then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))))
        .then(d => { this.song = new Song(d); this.plan = plan(this.song); })
        .catch(e => { this.err = String(e && e.message); console.warn('[jingle] no timings', this.err); });
      const audio = (Audio.has(KEY) ? Promise.resolve() : Audio.load({ [KEY]: Assets.url(AUDIO) })).then(() => { this.audioReady = Audio.has(KEY); });
      Promise.all([words, audio]).then(() => { if (!this.gone && !this.frozen && !this.startedOnce && this.song) this.play(0); });
      if (window.Device) {
        this.onHidden = Device.on('hidden', () => { if (this.playing && !this.gone) { this.pause(); this.resumeOnReturn = true; } });
        this.onResume = Device.on('resume', () => { if (this.resumeOnReturn && !this.gone && Game.current() === this) { this.resumeOnReturn = false; this.play(this.pos); } });
      }
    }
    exit() {
      this.gone = true; Audio.stopGroup('song'); this.playing = false;
      if (this.media) this.media.pause();
      if (window.Device) { Device.off('hidden', this.onHidden); Device.off('resume', this.onResume); }
    }
    get key() { return KEY; }
    /* the song clock: the <audio> element while it plays (t0ctx != null = "on the audio clock"), else pos */
    now() {
      if (this.playing && this.t0ctx != null) {
        const m = this.media;
        if (m) return m.playing() ? m.time() : m.el && m.el.ended ? this.song.dur : this.pos;
        if (Audio.running()) return Audio.now() - this.t0ctx;
      }
      return this.pos;
    }
    play(from = this.now()) {
      if (!this.song) return;
      this.startedOnce = true; this.frozen = false; this.ended = false;
      from = clamp(from, 0, this.song.dur - 0.05);
      Audio.stopGroup('song');
      this.pos = from; this.playing = true; this.t0ctx = null;
      if (!this.audioReady || window.__timeScale > 1) return;
      this.media = this.media || Audio.media(KEY);
      if (this.media) {
        const m = this.media, tok = this.playTok = (this.playTok || 0) + 1;
        this.playAt = Game.time; this.blocked = false;
        m.play(from).then(ok => {
          if (tok !== this.playTok || this.gone) return;
          if (ok && this.playing) { this.pos = m.time(); this.t0ctx = 0; }
          else if (!ok) { this.pause(); this.blocked = true; }
        });
      } else if (Audio.running()) {
        const at = Audio.now() + 0.06;
        Audio.schedule(KEY, { at, offset: from, gain: 1, group: 'song', bus: 'voice', fade: 0.03 });
        this.t0ctx = at - from;
      }
    }
    pause() {
      this.pos = this.now(); this.playing = false; this.t0ctx = null; this.playTok = (this.playTok || 0) + 1;
      Audio.stopGroup('song');
      if (this.media) this.media.pause();
    }
    toggle() { if (this.playing) this.pause(); else this.play(this.ended ? 0 : this.pos); Audio.play('paper', { gain: 0.25 }); }
    seek(t) { if (!this.song) return; const was = this.playing; this.pause(); this.pos = clamp(t, 0, this.song.dur); this.ended = false; if (was) this.play(this.pos); }
    restart() { this.heard = 0; this.play(0); }
    /* QA: freeze on song time t (no pause overlay) */
    debugAt(t) { this.pause(); this.frozen = true; this.startedOnce = true; this.ended = false; this.pos = t; }
    /* stamp the step now (QA / integration; the first full listen does this by itself) */
    done() { if (this.completed) return; this.completed = true; if (!Game.progress.stamps[STEP]) Game.complete(STEP); }
    lyricAt(t) { return this.song ? lyricAt(this.song, t) : null; }
    activeWord(t) { return this.song ? activeWord(this.song, t) : null; }
    actAt(t) { return this.plan ? this.plan.acts[actAt(this.plan, t)].id : null; }

    update(dt) {
      if (!this.song || !this.playing || this.scrub) return;
      if (this.t0ctx == null) {
        this.pos += dt * (window.__timeScale || 1);
        const retry = this.audioReady && !(window.__timeScale > 1) &&
          (this.media ? !this.blocked && Game.time - (this.playAt || 0) > 3 : Audio.running());
        if (retry) this.play(this.pos);
      }
      this.heard += dt * (this.t0ctx == null ? (window.__timeScale || 1) : 1);
      if (this.now() >= this.song.dur - 0.05) {
        this.pause(); this.pos = this.song.dur; this.ended = true;
        if (!this.completed && this.heard > 0.85 * this.song.dur) this.done();
      }
    }
    keyDown(k) {
      if (!this.song) return;
      if (k === ' ') this.toggle();
      else if (k === 'r' || k === 'R') this.restart();
      else if (k === 'ArrowRight') this.seek(this.now() + 5);
      else if (k === 'ArrowLeft') this.seek(this.now() - 5);
      else if (k === 'Enter' && this.ended) Game.replace(new Levels.map());
    }
    stripZone() { return Math.max(32, UI.tap(32)); }
    pointerDown(x, y) {
      if (!this.song || this.ended) return;
      if (UI.docked()) { this.toggle(); return; }
      if (UI.inHud(x, y, 3)) return;
      if (y > 1080 - this.stripZone()) {
        this.scrub = { was: this.playing };
        if (this.playing) this.pause();
        this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); this.ended = false;
        return;
      }
      this.toggle();
    }
    pointerMove(x) { if (this.scrub && this.song) this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); }
    pointerUp() {
      if (!this.scrub) return;
      const was = this.scrub.was; this.scrub = null;
      if (was) this.play(this.pos);
    }

    draw() {
      const docked = UI.docked(), title = (Game.content.jingle && Game.content.jingle.title) || 'Ask the Base';
      if (!this.song || !this.nums) {
        NB.ground();
        NB.title(title, 960, 420, { size: 110, t0: this.enteredAt + 0.2, hi: 'Base' });
        const msg = this.err ? 'the jingle is missing (' + this.err + ')' : 'tuning the radio…';
        NB.write(msg, 960, 580, { font: 'ui', size: 44, color: PN.pencil, align: 'center', t0: this.enteredAt + 0.4 });
        if (docked) { Dock.head(title, ''); Dock.text('jg-wait', [msg], { cls: 'note center' }); this.dockBar(); }
        else this.chrome(null);
        return;
      }
      const t = clamp(this.now(), 0, this.song.dur), gT = T, gBF = BF;
      T = t; BF = Math.floor(t * 8);
      try {
        drawActs(this.song, this.plan, t);
        cast(this.song, t, docked);
        if (!docked) band(this.song, t);
      } finally { T = gT; BF = gBF; }
      if (docked) this.dock(t, title); else this.chrome(t);
    }

    /* ---------- the portrait dock: karaoke, controls and the strip below the stage ---------- */
    dock(t, title) {
      const S = this.song, pl = this.plan;
      const mmss = s => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
      Dock.head(title, `${mmss(t)} / ${mmss(S.dur)}`);
      const { cur, next } = lyricAt(S, t);
      const sec = (cur || next || {}).section;
      const lastE = S.items.length ? S.items[S.items.length - 1].e : 0;
      if (cur) {
        const key = cur.i + ':' + cur.s;
        if (!this._dk || this._dk.key !== key) {
          const hk = hookIdx(cur);
          this._dk = { key, words: cur.words.map((w, j) => ({ w: w.w, s: w.s, e: w.e, hook: hk.has(j) })) };
        }
        Dock.karaoke({ key, words: this._dk.words, t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '' });
      } else Dock.karaoke({ key: 'gap', words: [], t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '',
        note: t > lastE && S.items.length ? 'Percent OF what? Ask the base.' : this.startedOnce || this.frozen ? '♪ ♫ ♪' : 'loading the jingle…' });
      Dock.text('credit', ['♪ ' + CREDIT], { cls: 'small center' });
      if (this.ended && Game.current() === this) {
        Dock.buttons('jg-end', [{ label: '↺ Play it again', onClick: () => this.restart() },
          { label: 'Back to the map', color: PN.ink, onClick: () => Game.replace(new Levels.map()) }]);
      }
      Dock.strip({ value: t, total: S.dur, ticks: pl.ticks, label: 'jingle position', onSeek: (v, phase) => this.scrubTo(v, phase) });
      this.dockBar();
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended && !this.scrub) this.pausedMark(540, 150);
    }
    dockBar() {
      if (this.song && !this.ended) {
        Dock.button({ id: 'restart', glyph: '↺', label: 'restart', aria: 'play the jingle from the start', onClick: () => this.restart() });
        Dock.button({ id: 'play', glyph: this.playing ? '❚❚' : '▶', label: this.playing ? 'pause' : 'play', aria: this.playing ? 'pause' : 'play', primary: true, onClick: () => this.toggle() });
      }
      Dock.button({ id: 'map', glyph: '⌂', label: 'map', aria: 'back to the map', onClick: () => Game.replace(new Levels.map()) });
    }
    scrubTo(v, phase) {
      if (!this.song) return;
      const pos = clamp(v, 0, this.song.dur - 0.05);
      if (phase === 'start') { this.scrub = { was: this.playing || (this.scrub && this.scrub.was) }; if (this.playing) this.pause(); this.ended = false; }
      this.pos = pos;
      if (phase === 'end') { const was = this.scrub ? this.scrub.was : false; this.scrub = null; if (was) this.play(this.pos); }
    }
    /* the paused ▶: a white wash and an ink circle */
    pausedMark(y, r) {
      g.save(); g.fillStyle = 'rgba(252,252,248,0.55)'; g.fillRect(0, 0, 1920, UI.docked() ? 1080 : BAND_Y); g.restore();
      g.save(); g.fillStyle = PN.ink; g.beginPath(); g.arc(960, y, r, 0, TAU); g.fill();
      g.fillStyle = PN.page; g.beginPath(); g.moveTo(960 - r * 0.3, y - r * 0.52); g.lineTo(960 + r * 0.52, y); g.lineTo(960 - r * 0.3, y + r * 0.52); g.closePath(); g.fill(); g.restore();
    }

    chrome(t) {
      const t0 = this.enteredAt, S = this.song;
      const intro = t == null || (this.plan && t < this.plan.acts[1].at);        // the intro page writes the big title itself
      if (!intro) NB.write('Ask the Base', 190, 124, { size: 50, weight: 700, t0: -1e9 });
      // a small ON AIR lamp beside the title: lit while a line is being sung
      const on = !!(S && t != null && S.singing(t));
      if (!intro) {
      g.save(); rrect(500, 104, 124, 40, 10); g.fillStyle = on ? PN.redpen : PN.card; g.fill(); g.lineWidth = 3; g.strokeStyle = PN.ink; g.stroke();
      g.font = `700 22px ${FONT.ui}`; g.fillStyle = on ? '#fff' : PN.pencilLight; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText('ON AIR', 562, 125); g.restore();
      }
      NB.write('♪ ' + CREDIT, 190, 176, { font: 'ui', size: 26, color: PN.pencil, t0: t0 + 0.4, dur: 0.3 });
      const m = UI.hudSlot(0), r = UI.hudSlot(1), pl = UI.hudSlot(2), fx = { r: m.r, fixed: true };
      if (UI.iconButton('jg-map', '⌂', m.x, m.y, fx)) { Game.replace(new Levels.map()); return; }
      if (S) {
        if (UI.iconButton('jg-restart', '↺', r.x, r.y, fx)) this.restart();
        if (UI.iconButton('jg-play', this.playing ? '❚❚' : '▶', pl.x, pl.y, { ...fx, primary: true })) this.toggle();
      }
      if (t == null) return;
      const zone = this.stripZone(), touch = zone > 32;
      if (!this.ended) UI.target('jg-strip', 960, 1080 - zone / 2, 1920, zone);
      const sh = touch ? 18 : 10, sy = 1080 - sh;
      g.save();
      g.fillStyle = PN.pencilLight; g.fillRect(0, sy, 1920, sh);
      g.fillStyle = PN.ink; g.fillRect(0, sy, 1920 * t / S.dur, sh);
      g.fillStyle = PN.redpen; this.plan.ticks.forEach(b => g.fillRect(1920 * b / S.dur - 2, sy - 6, 4, sh + 6));
      g.restore();
      if (touch || this.scrub) NB.pencil(clamp(1920 * t / S.dur, 26, 1894), sy, touch ? 0.9 : 0.6);
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended) {
        this.pausedMark(430, 100);
        NB.write(UI.hint('paused · click or Space to play', 'paused · tap to play'), 960, 590, { font: 'ui', size: 44, weight: 700, align: 'center' });
      }
      if (!this.startedOnce && !this.frozen) NB.write('loading the jingle…', 960, 590, { font: 'ui', size: 44, color: PN.pencil, align: 'center', t0: this.enteredAt + 0.3 });
      if (this.ended && Game.current() === this) {
        g.save(); g.fillStyle = 'rgba(252,252,248,0.6)'; g.fillRect(0, 380, 1920, 360); g.restore();
        if (UI.button('jg-again', '↺ Play it again', 700, 560, { w: 440, h: 110, size: 50, secondary: true })) this.restart();
        if (UI.button('jg-back', 'Back to the map', 1220, 560, { w: 440, h: 110, size: 50, primary: true })) Game.replace(new Levels.map());
      }
    }
  }
  Jingle.STEP = STEP;
  Jingle.TAKE = TAKE;
  Jingle.Song = Song;
  Jingle.plan = plan;
  Jingle.nums = nums;
  Levels.jingle = Jingle;
})();
